import type { DestinationContext, TrackingDestination } from '../types'

/**
 * Zoho PageSense event bridge.
 * The project snippet itself is installed in the root layout head so the anti-flicker
 * screen runs early enough. This destination only sends checkout identify/tag events.
 */
declare global {
  interface Window {
    pagesense?: any[]
  }
}

function isCheckoutPath(path: string | null | undefined) {
  if (!path) return false
  return path.startsWith('/checkout')
}

function effectivePathname(ctx: DestinationContext) {
  if (ctx.currentPath != null && ctx.currentPath !== '') return ctx.currentPath
  if (typeof window !== 'undefined') return window.location.pathname
  return ''
}

export class ZohoPageSenseDestination implements TrackingDestination {
  public readonly key = 'zohoPageSense' as const
  // Treat as analytics/session recording → gate behind statistics consent.
  public readonly requiredConsent = 'statistics' as const

  identify(userId: string, _traits?: Record<string, any>, ctx?: DestinationContext) {
    if (!ctx?.consent.statistics) return
    if (typeof window === 'undefined') return
    if (!isCheckoutPath(effectivePathname(ctx))) return
    window.pagesense = window.pagesense || []
    try {
      // Old-site: OrderSummary.ascx → pagesense.push(['identifyUser', email])
      window.pagesense.push(['identifyUser', userId])
    } catch {
      // ignore
    }
  }

  track(eventName: string) {
    if (typeof window === 'undefined') return
    window.pagesense = window.pagesense || []
    try {
      // Mirror old site behavior: pagesense.push(['tagRecording', 'Completed checkout'])
      window.pagesense.push(['tagRecording', eventName])
    } catch {
      // ignore
    }
  }
}
